class VariantPicker extends HTMLElement {
	connectedCallback() {
		const variantsJson = this.querySelector('script[data-variants]')?.textContent;
		this.variants = variantsJson ? JSON.parse(variantsJson) : [];
		this.addEventListener('change', this.onChange);
	}

	onChange = async () => {
		const selects = Array.from(this.querySelectorAll('select'));
		const selected = selects.map((s) => s.value);
		// section_id must be the section's rendered instance id (e.g.
		// "template--123__main"), not its file/type name ("main-product") —
		// the Section Rendering API 401s on a name that doesn't match an
		// actual section in the template.
		const sectionParam = `section_id=${encodeURIComponent(this.dataset.sectionId)}`;
		let sectionUrl;
		let pageUrl;
		if (selected.some((value) => value === '')) {
			// Placeholder re-selected: reset to the unselected state, same as
			// the app's goto(pathname). Without ?variant= the section renders
			// product.selected_variant == nil (placeholder buy box).
			selects.forEach((s) => { s.value = ''; });
			sectionUrl = `${this.dataset.url}?${sectionParam}`;
			pageUrl = this.dataset.url;
		} else {
			// The Section Rendering API only resolves a variant via
			// `?variant=<id>` — there's no `?option1=X&option2=Y` mechanism —
			// so look up the id from the embedded variants JSON by matching
			// each variant's own `options` array (same order as the <select>s).
			const match = this.variants.find(
				(v) => v.options.length === selected.length && v.options.every((o, i) => o === selected[i])
			);
			if (!match) return;
			sectionUrl = `${this.dataset.url}?variant=${match.id}&${sectionParam}`;
			pageUrl = `${this.dataset.url}?variant=${match.id}`;
		}
		const res = await fetch(sectionUrl);
		if (!res.ok) return;
		const html = await res.text();
		const fragment = new DOMParser().parseFromString(html, 'text/html');
		const newPrice = fragment.querySelector('.price');
		const currentPrice = document.querySelector('.price');
		if (newPrice && currentPrice) currentPrice.replaceWith(newPrice);

		// Swap the whole action row (favorite, shoppable-gate) instead of just
		// `.product__atc` so availability changes (e.g. available -> sold-out)
		// reconcile notify-me too. `<variant-picker>` is a sibling of
		// `.product__action-row`, never an ancestor, so this swap never removes
		// the element this handler runs on.
		const newActionRow = fragment.querySelector('.product__action-row');
		const currentActionRow = document.querySelector('.product__action-row');
		if (newActionRow && currentActionRow) currentActionRow.replaceWith(newActionRow);

		// The stepper shares a row with this picker, so it can't ride along with
		// the action-row swap — and it must be reconciled on its own: whether it
		// renders at all depends on the chosen variant's stock (>1 unit), and it
		// switches between the disabled "no variant yet" pill and a live one.
		// Replace it in place rather than swapping the row it lives in, which
		// would tear out the <variant-picker> running this handler.
		const controls = this.closest('.product__controls');
		const newQty = fragment.querySelector('.product__qty');
		const currentQty = controls?.querySelector('.product__qty');
		if (currentQty && newQty) currentQty.replaceWith(newQty);
		else if (currentQty) currentQty.remove();
		else if (newQty && controls) controls.append(newQty);

		const newVariantInput = fragment.querySelector('input[name=id]');
		const currentVariantInput = document.querySelector('#product-form input[name=id]');
		if (newVariantInput && currentVariantInput) currentVariantInput.value = newVariantInput.value;

		// Dimensions (and any other property) can be set per-variant, so the
		// Details panel must refresh alongside price/availability.
		const newProps = fragment.querySelector('.product__props');
		const currentProps = document.querySelector('.product__props');
		if (newProps && currentProps) currentProps.replaceWith(newProps);

		const featuredMediaId = fragment.querySelector('[data-featured-media-id]')?.dataset.featuredMediaId;
		if (featuredMediaId) {
			document.querySelector('product-gallery')?.dispatchEvent(
				new CustomEvent('variant:change', { detail: { featuredMediaId } })
			);
		}

		// Keep the address bar in sync (?variant=<id>) so reload / share /
		// back-forward land on the same selection, like the app's
		// goto(..., { replaceState: true }) URL-driven variants.
		history.replaceState(history.state, '', pageUrl);
	};
}
customElements.define('variant-picker', VariantPicker);
